import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowRight, Loader2, Mail, CheckCircle2 } from "lucide-react";
import { BrandLink } from "@/components/Brand";

/**
 * Public "Forgot password?" page. Linked from the login page footer.
 *
 * Security note: the API ALWAYS returns 200 — we never reveal whether the
 * email exists. The UI mirrors that contract: we show the same "check your
 * inbox" confirmation regardless of whether the address was registered, so
 * an attacker can't enumerate users by watching the response.
 */
export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setSent(true);
    } catch {
      // Even on a network failure we surface the "if it exists" message —
      // never leak existence via error messages.
      setSent(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2">
      <div className="hidden md:block relative overflow-hidden">
        <img
          src="https://images.unsplash.com/photo-1530046614490-89e6f776b83b?auto=format&fit=crop&w=1400&q=80"
          alt="Workshop"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-black/65" />
        <div className="relative h-full p-12 flex flex-col justify-between text-white">
          <div className="bg-card rounded-lg p-3 w-fit"><BrandLink /></div>
          <div>
            <div className="label-overline text-white/60">Forgot your password?</div>
            <h2 className="font-display text-4xl font-bold tracking-tight mt-3 leading-tight">
              No problem.<br />Let's get you <span className="text-primary">back in</span>.
            </h2>
          </div>
          <div className="font-mono-plex text-xs text-white/50">Reset link expires in 30 minutes · single use</div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-12 bg-background">
        {sent ? (
          <div className="w-full max-w-md space-y-5" data-testid="forgot-password-confirm">
            <div className="w-12 h-12 rounded-full bg-success/15 text-success grid place-items-center">
              <CheckCircle2 className="w-6 h-6" />
            </div>
            <div>
              <div className="label-overline">Check your inbox</div>
              <h1 className="font-display text-3xl font-bold tracking-tight mt-2">Reset link sent</h1>
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed">
              If an account exists for <span className="font-mono-plex text-foreground">{email}</span>,
              we've emailed you a one-time link to choose a new password. It'll expire
              in 30 minutes.
            </p>
            <p className="text-sm text-muted-foreground leading-relaxed">
              Didn't get it? Check your spam folder, or{" "}
              <button
                type="button"
                onClick={() => setSent(false)}
                className="text-primary font-semibold hover:underline"
                data-testid="forgot-password-try-again"
              >
                try again
              </button>.
            </p>
            <div>
              <Link to="/login" className="text-sm text-primary font-semibold hover:underline" data-testid="forgot-password-back-to-login">
                ← Back to sign in
              </Link>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="w-full max-w-md space-y-5" data-testid="forgot-password-form">
            <div>
              <div className="label-overline">Forgot password</div>
              <h1 className="font-display text-3xl font-bold tracking-tight mt-2">Reset your password</h1>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
                Enter the email you signed up with and we'll send you a reset link.
              </p>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="input-default pl-9"
                  placeholder="you@dealership.co.uk"
                  data-testid="forgot-password-email-input"
                />
              </div>
            </div>
            <button type="submit" disabled={busy || !email} className="btn-primary w-full" data-testid="forgot-password-submit-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Send reset link <ArrowRight className="w-4 h-4" /></>}
            </button>
            <p className="text-sm text-muted-foreground">
              Remembered it? <Link to="/login" className="text-primary font-semibold hover:underline" data-testid="forgot-password-to-login-link">Back to sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
